'use client';

import { sendVerificationCode, verifyCode } from '@/lib/mfa-client';
import { normalizePhoneNumber } from '@/utils/phone';
import { useEffect, useRef, useState } from 'react';
import { useCountUp } from 'react-countup';

// --- SVG Star Components ---
const StarIcon = ({ fill = 'currentColor', className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill={fill}
    className={className}
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z"
      clipRule="evenodd"
    />
  </svg>
);

const HalfStar = ({ className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    className={className}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="halfGradient">
        <stop offset="50%" stopColor="#18181b" /> {/* Zinc-900 */}
        <stop offset="50%" stopColor="#d1d5db" /> {/* Gray-300 */}
      </linearGradient>
    </defs>
    <path
      fillRule="evenodd"
      d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z"
      clipRule="evenodd"
      fill="url(#halfGradient)"
    />
  </svg>
);

// --- Resend/Refresh Icon Component ---
const RefreshIcon = ({ className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

// --- Loader Icon ---
const LoaderIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    className={`animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

// --- End SVG Components ---

const FormattedMessage = ({ content }: { content: string }) => {
  if (!content) return null;

  // Split by one or more newlines to create paragraphs
  const paragraphs = content.split(/\n\n|\n\n/);

  return (
    <>
      {paragraphs.map((paragraph, index) => {
        // Replace **...** with <strong>...</strong> for bolding
        const htmlContent = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        const className = index < paragraphs.length - 1 ? 'mb-3' : '';
        return (
          <p key={index} className={className} dangerouslySetInnerHTML={{ __html: htmlContent }} />
        );
      })}
    </>
  );
};

interface ValuationFormProps {
  dictionary: any;
  phoneNumber: string;
  priceRange?: {
    min: number;
    max: number;
  };
  isVerified: boolean;
  setIsVerified: (verified: boolean) => void;
  onGoBack: () => void;
  onVerificationSuccess: () => void;
  onDetailsResponse: (response: 'result') => void;
  onEmailSubmit: (email: string) => void;
  initialStarRating?: number;
  isSubmitted: boolean;
  enableEmailCollection?: boolean;
  enableEmailCollectionOnlyAgency?: boolean;
  customEmailMessage?: string;
  customEmailPrompt?: string;
  customThankYouMessage?: string;
  // NEW: Custom valuation component props
  enableCustomValuationComponent?: boolean;
  customValuationMessage?: string;
  customValuationButton?: string;
  customValuationTimer?: number;
  onCustomValuationSubmit?: () => void;
  // Agent contact notice control
  agentContactNoticeNull?: boolean;
  // Locale for API calls
  locale?: string;
  // Valuation confirmation (blurred value)
  enableValuationConfirmation?: boolean;
  onValuationConfirmationResponse?: (wantsContact: boolean) => void;
  initialValuationConfirmed?: boolean | null;
  // Skip valuation display and show contact message instead
  noValuation?: boolean;
}

export default function ValuationForm({ dictionary, priceRange, phoneNumber, isVerified, setIsVerified, onGoBack, onVerificationSuccess, onDetailsResponse, onEmailSubmit, initialStarRating, isSubmitted, enableEmailCollection, enableEmailCollectionOnlyAgency, customEmailMessage, customEmailPrompt, customThankYouMessage, enableCustomValuationComponent, customValuationMessage, customValuationButton, customValuationTimer, onCustomValuationSubmit, agentContactNoticeNull, locale = 'en', enableValuationConfirmation, onValuationConfirmationResponse, initialValuationConfirmed, noValuation }: ValuationFormProps) {
  const [animationFinished, setAnimationFinished] = useState(false);
  const [customValuationSubmitted, setCustomValuationSubmitted] = useState(false);
  
  // Valuation confirmation state
  // Start unblurred - blur will be applied during animation if needed
  // If user already answered (true or false), they should see the value
  const [isValueBlurred, setIsValueBlurred] = useState(false);
  const [showConfirmationCard, setShowConfirmationCard] = useState(false);
  const [confirmationComplete, setConfirmationComplete] = useState(
    initialValuationConfirmed !== null
  );

  // No valuation animation state
  const [showNoValuationText, setShowNoValuationText] = useState(false);

  // Refs for the HTML elements where numbers will be displayed
  const minValRef = useRef<HTMLSpanElement>(null);
  const maxValRef = useRef<HTMLSpanElement>(null);

  // --- Verification State (from stashed version) ---
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);

  // --- Timer State ---
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // --- End Timer State ---

  // --- Email Form State ---
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>(isSubmitted ? 'success' : 'idle');
  const [showEmailForm, setShowEmailForm] = useState(false);
  // --- End Email Form State ---

  // Use the hook, passing the ref and configuration
  const { start: startMinCountUp } = useCountUp({
    ref: minValRef, // Target the span element
    start: 0,
    end: priceRange?.min ?? 0, // Use default 0 if priceRange is invalid
    duration: 3,
    separator: '.',
    decimal: ',',
    decimals: 0,
    startOnMount: false, // Don't start automatically
    onEnd: () => setAnimationFinished(true) // Trigger pulse on end
  });

  const { start: startMaxCountUp } = useCountUp({
    ref: maxValRef, // Target the span element
    start: 0,
    end: priceRange?.max ?? 0, // Use default 0 if priceRange is invalid
    duration: 3,
    separator: '.',
    decimal: ',',
    decimals: 0,
    startOnMount: false, // Don't start automatically
    onEnd: () => setAnimationFinished(true) // Trigger pulse on end
  });

  // --- Timer Effect ---
  useEffect(() => {
    if (isCodeSent && !canResend) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            if (timerRef.current) {
              clearInterval(timerRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isCodeSent, canResend]);

  // --- Effect to start animation after a delay - ONLY IF VERIFIED ---
  useEffect(() => {
    if (isVerified) { // Only run if verified
      const timer = setTimeout(() => {
        // Call the start functions returned by the hooks
        startMinCountUp();
        startMaxCountUp();
      }, 500); // 500ms delay

      // Cleanup timeout if component unmounts before delay finishes
      return () => clearTimeout(timer);
    }
  }, [isVerified, startMinCountUp, startMaxCountUp]); // Add isVerified dependency

  // --- Effect: Apply blur 0.5s BEFORE animation finishes (for valuation confirmation) ---
  useEffect(() => {
    if (isVerified && enableValuationConfirmation && initialValuationConfirmed === null) {
      // Animation: 500ms delay + 3000ms duration = 3500ms total
      // Blur at: 3500ms - 500ms = 3000ms from verification
      const blurTimer = setTimeout(() => {
        setIsValueBlurred(true);
      }, 3000); // 3 seconds after verification (0.5s before animation ends)

      return () => clearTimeout(blurTimer);
    }
  }, [isVerified, enableValuationConfirmation, initialValuationConfirmed]);

  // --- Effect: Show no valuation text with smooth transition ---
  useEffect(() => {
    if (isVerified && noValuation) {
      // Match the timing of regular valuation animation (500ms delay + some buffer)
      const timer = setTimeout(() => {
        setShowNoValuationText(true);
      }, 500); // 500ms delay to match the feel of the animation starting

      return () => clearTimeout(timer);
    }
  }, [isVerified, noValuation]);

  // --- NEW Effect: Show details section after animation finishes OR after no valuation animation ---
  useEffect(() => {
    // For noValuation, show email form after no valuation text animation completes
    if (noValuation && showNoValuationText && (enableEmailCollection || enableEmailCollectionOnlyAgency)) {
      // Add a small delay after the no valuation text appears to make it feel natural
      const timer = setTimeout(() => {
        setShowEmailForm(true);
      }, 200); // 200ms delay after no valuation text animation completes
      return () => clearTimeout(timer);
    }

    // For standard valuation with animation
    if (animationFinished) {
      if (enableValuationConfirmation && initialValuationConfirmed === null) {
        // Show confirmation card after animation completes (blur already applied)
        const cardTimer = setTimeout(() => {
          setShowConfirmationCard(true);
        }, 500); // Card appears 500ms after animation ends

        return () => clearTimeout(cardTimer);
      } else {
        // Original behavior - show email form
        const timer = setTimeout(() => {
          setShowEmailForm(true);
        }, 500); // 500ms delay after animation ends
        return () => clearTimeout(timer); // Cleanup timer
      }
    }
  }, [animationFinished, enableValuationConfirmation, initialValuationConfirmed, noValuation, showNoValuationText, enableEmailCollection, enableEmailCollectionOnlyAgency]); // Added showNoValuationText dependency

  // --- Verification Handlers (from stashed version) ---
  const localizeError = (message: string, phase: 'send' | 'verify'): string => {
    const msg = String(message || '').toLowerCase();
    if (phase === 'verify') {
      if (msg.includes('expired')) {
        return (
          dictionary?.sellingForm?.verificationInvalidOrExpiredCode ||
          dictionary?.sellingForm?.verificationInvalidCode ||
          'Invalid or expired verification code'
        );
      }
      if (msg.includes('invalid') && msg.includes('verification') && msg.includes('code')) {
        return (
          dictionary?.sellingForm?.verificationInvalidCode ||
          'Invalid verification code'
        );
      }
      // Generic fallback for verify phase
      return (
        dictionary?.sellingForm?.verificationInvalidCode ||
        'Invalid verification code'
      );
    }

    // send phase
    if (msg.includes('invalid') && msg.includes('phone')) {
      return (
        dictionary?.sellingForm?.invalidPhoneFormat ||
        dictionary?.sellingForm?.invalidPhone ||
        'Invalid phone format'
      );
    }
    return (
      dictionary?.sellingForm?.verificationFailedToSend ||
      'Failed to send verification code'
    );
  };

  const handleSendCode = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Normalize phone number before sending
      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      
      // Validate Belgian phone number format more strictly
      if (!normalizedPhone) {
        throw new Error(
          dictionary?.sellingForm?.invalidBelgianPhone || 'Please enter a valid Belgian phone number (e.g. +32 4 12 34 56 78)'
        );
      }
      
      if (phoneNumber.startsWith('+32')) {
        const digits = phoneNumber.substring(3).replace(/\D/g, ''); // Remove +32 and non-digits
        const mobilePartIndex = digits.indexOf('4');
        
        if (mobilePartIndex === -1) {
          // No '4' found - not a valid Belgian mobile
          throw new Error(
            dictionary?.sellingForm?.invalidBelgianPhone || 'Please enter a valid Belgian phone number (e.g. +32 4 12 34 56 78)'
          );
        } else {
          const nationalPart = digits.substring(mobilePartIndex);
          if (nationalPart.length !== 9) {
            // Not exactly 9 digits starting with 4
            throw new Error(
              dictionary?.sellingForm?.invalidBelgianPhone || 'Please enter a valid Belgian phone number (e.g. +32 4 12 34 56 78)'
            );
          }
        }
      } else if (normalizedPhone === phoneNumber) {
        // If normalization didn't change the number and it doesn't start with +32, it might be invalid
        throw new Error(
          dictionary?.sellingForm?.invalidBelgianPhone || 'Please enter a valid Belgian phone number (e.g. +32 4 12 34 56 78)'
        );
      }

      const response = await sendVerificationCode(normalizedPhone, locale);

      if (!response.success) {
        throw new Error(
          response.error || dictionary?.sellingForm?.verificationFailedToSend || 'Failed to send verification code'
        );
      }

      setIsCodeSent(true);
      // Reset timer state for resend
      setTimeLeft(60);
      setCanResend(false);
    } catch (error: any) {
      setError(localizeError(error.message, 'send'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setError(dictionary?.sellingForm?.verificationCodeRequired || 'Verification code is required');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      // Use normalized phone number for verification
      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      const response = await verifyCode(normalizedPhone, verificationCode, locale);

      if (!response.success) {
        throw new Error(
          response.error || dictionary?.sellingForm?.verificationInvalidCode || 'Invalid verification code'
        );
      }

      setIsVerified(true);
      onVerificationSuccess();
      onDetailsResponse('result');
    } catch (error: any) {
      setError(localizeError(error.message, 'verify'));
    } finally {
      setIsVerifying(false);
    }
  };

  // --- Resend Code Handler ---
  const handleResendCode = async () => {
    await handleSendCode(); // Reuse the existing send code logic
  };
  // --- End Verification Handlers ---

  // --- Email Submit Handler ---
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Call the parent handler to trigger background tasks
    onEmailSubmit(email);

    // After a short delay to feel responsive, update the UI to show success
    setTimeout(() => {
      setSubmitStatus('success');
      setIsSubmitting(false);
    }, 500);
  };
  // --- End Email Submit Handler ---

  // --- Custom Valuation Submit Handler ---
  const handleCustomValuationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCustomValuationSubmitted(true);
    onCustomValuationSubmit?.();
  };
  // --- End Custom Valuation Submit Handler ---

  // --- Valuation Confirmation Handlers ---
  const handleYesClick = () => {
    setIsValueBlurred(false);
    setConfirmationComplete(true);
    setShowConfirmationCard(false);
    onValuationConfirmationResponse?.(true);
    // Show email form after delay
    setTimeout(() => setShowEmailForm(true), 500);
  };

  const handleNoClick = () => {
    setIsValueBlurred(false); // Unblur the value - they answered the question!
    setConfirmationComplete(true);
    setShowConfirmationCard(false);
    onValuationConfirmationResponse?.(false);
    // Show email form after unblurring
    setTimeout(() => setShowEmailForm(true), 500);
  };
  // --- End Valuation Confirmation Handlers ---

  if (!priceRange || typeof priceRange.min !== 'number' || typeof priceRange.max !== 'number') {
    return (
      <div className="space-y-2">
        <div>
          <label htmlFor="valuation" className="block text-base text-zinc-800">
            {dictionary?.sellingForm?.valuationDisplayDescription || 'Estimating the value of your property...'}
          </label>
          <div className="mt-2 p-3 bg-zinc-100 rounded-lg min-h-[44px]">
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isVerified ? (
        // --- Verification UI ---
        <div className="space-y-4">
          <div>
            <label htmlFor="verification" className="block text-base md:text-sm font-medium text-zinc-900">
              {dictionary?.sellingForm?.verificationTitle || 'Phone Verification'}
            </label>
            <p className="mt-1 text-base md:text-sm text-zinc-700">
              {dictionary?.sellingForm?.verificationDescription || 'We need to verify your phone number before showing the valuation.'}
            </p>
          </div>

          {!isCodeSent ? (
            // --- Stack buttons vertically --- 
            <div className="flex flex-col gap-2 items-start">
              <button
                onClick={handleSendCode}
                disabled={isLoading}
                // Adjusted classes for vertical stacking
                className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none"
              >
                {isLoading
                  ? (dictionary?.sellingForm?.sendingCodeButton || 'Sending...')
                  : (dictionary?.sellingForm?.sendCodeButton || 'Send Verification Code')}
              </button>
              <button
                type="button"
                onClick={onGoBack}
                disabled={isLoading}
                // Adjusted classes for vertical stacking
                className="text-zinc-600 py-1 text-sm hover:text-zinc-900 disabled:opacity-50 disabled:pointer-events-none"
              >
                {/* --- Render dictionary string as HTML --- */}
                <span dangerouslySetInnerHTML={{ __html: dictionary?.sellingForm?.changePhoneButton || '<u>Change Number</u>' }} />
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder={dictionary?.sellingForm?.verificationCodePlaceholder || 'Enter verification code'}
                  className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow"
                />
              </div>

              {/* Timer and Resend Section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {!canResend ? (
                    <p className="text-xs text-zinc-600">
                      {(dictionary?.sellingForm?.resendCodeTimer || `New code in ${timeLeft}s`).replace('{timeLeft}', timeLeft.toString())}
                    </p>
                  ) : (
                    <button
                      onClick={handleResendCode}
                      disabled={isLoading}
                      className="flex items-center space-x-1 text-xs text-zinc-600 underline transition-colors hover:text-zinc-900 disabled:opacity-50"
                    >
                      <RefreshIcon className="w-3 h-3" />
                      <span>{dictionary?.sellingForm?.resendCodeButton || 'Resend code'}</span>
                    </button>
                  )}
                </div>
              </div>

              <button
                onClick={handleVerifyCode}
                disabled={isVerifying}
                className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none"
              >
                {isVerifying
                  ? (dictionary?.sellingForm?.verifyingCodeButton || 'Verifying...')
                  : (dictionary?.sellingForm?.verifyCodeButton || 'Verify Code')}
              </button>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-sm mt-2">
              {error}
            </div>
          )}
        </div>
        // --- End Verification UI ---
      ) : (
        // --- Existing Valuation Display (from current version) ---
        <div className="space-y-2">
          <div>
            {noValuation ? (
              // --- No Valuation Message Display ---
              <>

                <div className={`p-1 bg-white min-h-[44px] transition-all duration-500 ease-out ${showNoValuationText ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                  <p className="text-base text-zinc-800">
                    {dictionary?.sellingForm?.noValuationMessage || 'Bedankt! Eén van onze makelaars neemt spoedig contact met je op om je schatting te bespreken.'}
                  </p>
                </div>
              </>
            ) : (
              // --- Standard Valuation Display ---
              <>
                <label htmlFor="valuation" className="block text-base md:text-sm text-zinc-800">
                  {dictionary?.sellingForm?.valuationDisplayDescription || 'Thank you! Based on all the information you\'ve given me, the estimated value of your property is:'}
                </label>
                <div className="mt-3 px-3 py-4 bg-white rounded-lg shadow-sm border border-gray-200 min-h-[44px]">
                  {/* Render spans and attach refs. The hook will populate them. */}
                  <p className={`text-2xl font-semibold text-zinc-900 text-center ${animationFinished ? 'animate-pulse-reveal' : ''} ${isValueBlurred && enableValuationConfirmation ? 'blur-md select-none' : ''}`}>
                    €<span ref={minValRef}>0</span> - €<span ref={maxValRef}>0</span>
                  </p>
                  {/* Add bordered star container below the price range, visible only after animation */}
                  {animationFinished && initialStarRating && initialStarRating > 0 && ( // Conditionally render only when animation done and stars calculated
                    <div className="mt-3 flex items-center justify-center space-x-2"> {/* Container for label and stars */}
                      {/* Label */}
                      <p className="text-sm text-zinc-700 mt-0.5 md:test-xs md:mt-1">
                        {dictionary?.sellingForm?.comparablesAccuracyLabel || 'Accuracy'}
                      </p>
                      {/* Star container */}
                      <div className="flex items-center space-x-0.5"> {/* Adjusted spacing for SVGs */}
                        {[1, 2, 3, 4, 5].map((index) => {
                          if (index <= initialStarRating) {
                            // Full yellow star
                            return <StarIcon key={index} className="w-4 h-4 text-zinc-900" />;
                          } else if (index === Math.ceil(initialStarRating) && initialStarRating % 1 !== 0) {
                            // Half star
                            return <HalfStar key={index} className="w-4 h-4" />;
                          } else {
                            // Empty grey star
                            return <StarIcon key={index} className="w-4 h-4 text-gray-300" />;
                          }
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            {!agentContactNoticeNull && (
              <p className="text-[10px] text-zinc-500 my-2 text-center">
                {dictionary?.sellingForm?.agentContactNotice || 'A real estate agent may contact you.'}
              </p>
            )}
          </div>

          {/* Blurred Value Confirmation Card */}
          {showConfirmationCard && enableValuationConfirmation && (
            <div className="mt-4 p-4 bg-white border border-zinc-300 rounded-lg shadow-sm">
              {/* Unlock icon and text */}
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" 
                  />
                </svg>
                <p className="text-xs text-zinc-600">
                  {dictionary?.sellingForm?.valuationConfirmationUnlockText || 
                    'Just one last question to unlock the value.'}
                </p>
              </div>
              
              <p className="text-sm text-zinc-800 mb-4 leading-relaxed">
                {dictionary?.sellingForm?.valuationConfirmationMessage || 
                  'May we contact you without obligation to refine the AI value together?'}
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleYesClick}
                  className="flex-1 px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium hover:bg-zinc-700 transition-colors"
                >
                  {dictionary?.sellingForm?.valuationConfirmationYes || 'Yes, please'}
                </button>
                <button
                  onClick={handleNoClick}
                  className="flex-1 px-4 py-2.5 bg-white text-zinc-800 border border-zinc-300 rounded-lg text-sm font-medium hover:bg-zinc-50 transition-colors"
                >
                  {dictionary?.sellingForm?.valuationConfirmationNo || 'No'}
                </button>
              </div>
            </div>
          )}

          {/* Success message */}
          {confirmationComplete && enableValuationConfirmation && !showConfirmationCard && (
            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md font-medium mt-4">
              {dictionary?.sellingForm?.valuationConfirmationSuccess || 
                'Thank you for your answer!'}
            </p>
          )}
        </div>
        // --- End Existing Valuation Display ---
      )}

      {/* --- START Email Form Section --- */}
      {showEmailForm && (enableEmailCollection || enableEmailCollectionOnlyAgency) && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          {submitStatus === 'success' ? (
            <p className="text-base text-green-700 bg-green-50 p-3 rounded-md font-medium">
              {enableEmailCollectionOnlyAgency 
                ? (dictionary?.sellingForm?.[customThankYouMessage || 'customThankYouMessage'] || 'Thank you! We will contact you soon.')
                : dictionary?.sellingForm?.emailSuccessResponse || 'Thank you! We will send you the detailed valuation shortly.'
              }
            </p>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <label htmlFor="email-input" className="block text-base text-zinc-800">
                {enableEmailCollectionOnlyAgency
                  ? (dictionary?.sellingForm?.[customEmailMessage || 'customEmailMessage'] || 'Would you be interested in receiving a detailed valuation report?')
                  : dictionary?.sellingForm?.emailRequestPrompt || 'Drop your email below if you want to receive a detailed valuation of your property.'
                }
              </label>
              <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
                <input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={enableEmailCollectionOnlyAgency
                    ? (dictionary?.sellingForm?.[customEmailPrompt || 'customEmailPrompt'] || 'Please provide your email address:')
                    : dictionary?.sellingForm?.emailPlaceholder || 'your@email.be'
                  }
                  required
                  className="flex-grow w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto flex-shrink-0 px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <LoaderIcon className="h-5 w-5" />
                  ) : (
                    dictionary?.sellingForm?.sendButton || 'Send'
                  )}
                </button>
              </div>
              {submitStatus === 'error' && (
                <p className="text-red-600 text-sm">
                  {dictionary?.sellingForm?.emailErrorResponse || 'Something went wrong. Please try again.'}
                </p>
              )}
            </form>
          )}
        </div>
      )}
      {/* --- END Email Form Section --- */}

      {/* --- START Custom Valuation Component --- */}
      {showEmailForm && enableCustomValuationComponent && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          {customValuationSubmitted ? (
            <p className="text-base text-green-700 bg-green-50 p-3 rounded-md font-medium">
              {dictionary?.sellingForm?.customValuationThankYou || 'Thank you! We will contact you soon.'}
            </p>
          ) : (
            <div className="space-y-3">
              <div className="text-base text-zinc-800">
                <FormattedMessage 
                  content={dictionary?.sellingForm?.[customValuationMessage || 'customValuationMessage'] || 
                    'This is an AI-driven estimate based on online reference points. Would you like to be contacted by an agent for an objective valuation?'} 
                />
              </div>
              <button
                onClick={handleCustomValuationSubmit}
                className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none"
              >
                {dictionary?.sellingForm?.[customValuationButton || 'customValuationButton'] || 
                 'Yes'}
              </button>
            </div>
          )}
        </div>
      )}
      {/* --- END Custom Valuation Component --- */}
    </div>
  );
} 